import { ImageErrorType } from "@next-platform/packages-ts-types";
import { useCallback, useState } from "react";
import { compareErrorCode } from "../../utils";
import { useAppDispatch, useAppSelector } from "../hook";
import { useTranslation } from "../localization";
import { ModerationFlows, RateLimitFlows, useModerationErrors, useRateLimitErrors } from "../miscellaneous";
import { usePersona, useUserInfo, useUserSettings } from "../user";
import { ABORT_ERROR_MESSAGE } from "./constants";
import conversationLogger from "./conversationLogger";
import { deleteMessageImage, generateConversationImage, generateMessage, getCharacterMessagesThunk, rateMessage, updateMessage, } from "./conversationSlice";
import { ChatRole, ConversationFlow } from "./types";
import { useConversationErrors } from "./useConversationErrors";
export function useConversation({ conversationId, characterId }) {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { getMessage: getRateLimitErrorMessage, getTitle: getRateLimitTitle } = useRateLimitErrors();
    const { getMessage: getModerationErrorMessage } = useModerationErrors();
    const { getMessage: getConversationErrorMessage } = useConversationErrors();
    const { selectedPersona } = usePersona();
    const { userId } = useUserInfo();
    const { userChatLanguage } = useUserSettings();
    const { selectedMessageIndex, isLoading, loadingType } = useAppSelector((state) => state.conversation);
    const [conversationError, setConversationError] = useState(null);
    const [isAbortError, setIsAbortError] = useState(false);
    const resetError = () => {
        setConversationError(null);
    };
    const getCharacterMessages = useCallback(async (params) => {
        try {
            setConversationError(null);
            const data = await dispatch(getCharacterMessagesThunk(params)).unwrap();
            return { data, error: null };
        }
        catch (error) {
            const apiError = error;
            setConversationError({
                errorType: ConversationFlow.GET_MESSAGE,
                errorMessage: apiError?.message || t("error:fallbackError"),
                errorCode: apiError?.errorCode,
            });
            conversationLogger.error(`Error while getting character messages: ${apiError?.message}`, apiError);
            return {
                data: null,
                error: apiError?.message || t("error:fallbackError"),
                errorPayload: apiError,
            };
        }
    }, [dispatch, t]);
    const onUserUpdateMessage = useCallback(async (params) => {
        try {
            setConversationError(null);
            const data = await dispatch(updateMessage(params)).unwrap();
            return { data, error: null };
        }
        catch (error) {
            const apiError = error;
            const conversationErrorMessage = getConversationErrorMessage({
                errorCode: apiError?.errorCode,
                errorContext: {
                    flow: ConversationFlow.UPDATE_MESSAGE,
                },
                skipDefaultMessage: true,
            });
            const errorMessage = conversationErrorMessage || t("error:fallbackError");
            setConversationError({
                errorType: ConversationFlow.UPDATE_MESSAGE,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            conversationLogger.error(`Error while updating conversation message: ${apiError?.message}`, apiError);
            return {
                data: null,
                error: errorMessage,
                errorPayload: apiError,
            };
        }
    }, [dispatch, getConversationErrorMessage, t]);
    const onUserDeleteMessageImage = useCallback(async (messageId) => {
        try {
            setConversationError(null);
            const data = await dispatch(deleteMessageImage(messageId)).unwrap();
            return { data, error: null };
        }
        catch (error) {
            const apiError = error;
            const conversationErrorMessage = getConversationErrorMessage({
                errorCode: apiError?.errorCode,
                errorContext: {
                    flow: ConversationFlow.DELETE_MESSAGE_IMAGE,
                },
                skipDefaultMessage: true,
            });
            const errorMessage = conversationErrorMessage || apiError?.message || t("error:fallbackError");
            setConversationError({
                errorType: ConversationFlow.DELETE_MESSAGE_IMAGE,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            conversationLogger.error(`Error while deleting message image: ${apiError?.message}`, apiError);
            return {
                data: null,
                error: errorMessage,
                errorPayload: apiError,
            };
        }
    }, [dispatch, getConversationErrorMessage, t]);
    const onUserGenerateMessage = useCallback(async (messagePayload) => {
        try {
            setConversationError(null);
            setIsAbortError(false);
            const payload = {
                conversation_id: conversationId,
                character_id: characterId,
                user_persona_id: selectedPersona?.id,
                language: userChatLanguage,
                ...messagePayload,
            };
            const data = await dispatch(generateMessage(payload)).unwrap();
            return { data, error: null };
        }
        catch (error) {
            const apiError = error;
            const isAbortError = apiError.message === ABORT_ERROR_MESSAGE;
            setIsAbortError(isAbortError);
            if (isAbortError) {
                return {
                    data: null,
                    error: ABORT_ERROR_MESSAGE,
                    errorPayload: apiError,
                };
            }
            const conversationErrorMessage = getConversationErrorMessage({
                errorCode: apiError?.errorCode,
                errorContext: {
                    flow: ConversationFlow.GENERATE_MESSAGE,
                },
                skipDefaultMessage: true,
            });
            const rateLimitErrorMessage = getRateLimitErrorMessage({
                errorCode: apiError?.errorCode,
                errorContext: {
                    flow: RateLimitFlows.CHAT,
                },
                skipDefaultMessage: true,
            });
            const errorMessage = conversationErrorMessage || rateLimitErrorMessage || t("error:fallbackError");
            setConversationError({
                errorType: ConversationFlow.GENERATE_MESSAGE,
                errorCode: apiError?.errorCode,
                errorMessage: errorMessage,
            });
            if (!isAbortError) {
                conversationLogger.error(`Error while generating text message: ${errorMessage}`, { chatbot_id: characterId, user_id: userId }, apiError);
            }
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, getConversationErrorMessage, getRateLimitErrorMessage, conversationId, characterId, selectedPersona, userChatLanguage, userId, t]);
    const onUserGenerateConversationImage = useCallback(async (imagePayload) => {
        try {
            setConversationError(null);
            const payload = {
                conversation_id: conversationId,
                character_id: characterId,
                user_persona_id: selectedPersona?.id,
                ...imagePayload,
            };
            const data = await dispatch(generateConversationImage(payload)).unwrap();
            return { data, error: null };
        }
        catch (error) {
            const apiError = error;
            const moderationErrorMessage = getModerationErrorMessage({
                errorCode: apiError?.errorCode,
                skipDefaultMessage: true,
                errorContext: {
                    flow: ModerationFlows.GENERATE_CONVERSATION_IMAGE,
                },
            });
            const rateLimitErrorMessage = getRateLimitErrorMessage({
                errorCode: apiError?.errorCode,
                skipDefaultMessage: true,
                errorContext: {
                    flow: RateLimitFlows.GENERATE_CONVERSATION_IMAGE,
                },
            });
            const rateLimitTitle = getRateLimitTitle({
                errorCode: apiError?.errorCode,
                errorContext: {
                    flow: RateLimitFlows.GENERATE_CONVERSATION_IMAGE,
                },
            });
            const errorMessage = moderationErrorMessage || rateLimitErrorMessage || apiError?.message || t("error:fallbackError");
            if (compareErrorCode(apiError?.errorCode, ImageErrorType.NSFW_CONTENT_NOT_ALLOWED_FOR_UNVERIFIED_USERS)) {
                return {
                    data: {
                        message: {
                            conversation_id: conversationId || "",
                            createdAt: new Date().getTime(),
                            id: "",
                            prev_id: "",
                            role: ChatRole.BOT,
                            is_nsfw: true, // this is the property that will be used to show the NSFW warning & blurred image in the frontend
                            content: "",
                        },
                    },
                    error: null,
                };
            }
            setConversationError({
                errorType: ConversationFlow.GENERATE_CONVERSATION_IMAGE,
                errorCode: apiError?.errorCode,
                errorMessage: errorMessage,
                errorTitle: rateLimitTitle || undefined,
            });
            conversationLogger.error(`Error while generating conversation image: ${errorMessage}`, { chatbot_id: characterId, user_id: userId }, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, conversationId, characterId, getModerationErrorMessage, getRateLimitErrorMessage, getRateLimitTitle, selectedPersona, userId, t]);
    const onUserRateMessage = useCallback(async (rateMessagePayload) => {
        try {
            setConversationError(null);
            const data = await dispatch(rateMessage(rateMessagePayload)).unwrap();
            return { data, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError?.message || t("error:fallbackError");
            setConversationError({
                errorType: ConversationFlow.RATE_MESSAGE,
                errorCode: apiError?.errorCode,
                errorMessage: errorMessage,
            });
            conversationLogger.error(`Error while rating message: ${errorMessage}`, apiError);
            return {
                data: null,
                error: errorMessage,
                errorPayload: apiError,
            };
        }
    }, [dispatch, characterId, userId, t]);
    return {
        onUserGenerateMessage,
        onUserGenerateConversationImage,
        onUserRateMessage,
        getCharacterMessages,
        onUserUpdateMessage,
        onUserDeleteMessageImage,
        selectedMessageIndex,
        errorType: conversationError?.errorType || null,
        errorMessage: conversationError?.errorMessage || null,
        errorTitle: conversationError?.errorTitle || null,
        errorCode: conversationError?.errorCode || null,
        resetError,
        isAbortError,
        isLoading,
        loadingType,
    };
}
